import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ToggleButton, ToggleButtonGroup } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { MetricLensToggle } from '../MetricLensToggle'
import { TrackedSetDisclaimer } from '../TrackedSetDisclaimer/TrackedSetDisclaimer'
import { TrendsChartSeriesItem, TrendsLineChartCard } from '../TrendsLineChartCard'
import { CatalogueMetricKey } from '../helpers/catalogueUtils'
import { compactToggleGroupSx } from '../helpers/toggleStyles'
import { TrendsSelectedPeriod } from '../helpers/trendsSelectedPeriod'
import { TrendsSeriesSourceWithDenominator, pickTrendsSeriesForMetric } from '../helpers/trendsSeries'

export type LevelTrendsChartMode = 'total' | 'split'

export interface LevelTrendsChartSlots {
  tabs: React.ReactNode
  headerControls: React.ReactNode
  footer: React.ReactNode
  embedded: boolean
}

export interface LevelTrendsChartSplit {
  // Toggle label, e.g. "Split by blueprint families".
  label: string
  // When set, the split option is disabled and explains why on hover.
  unavailableMessage?: string
  // Renders the child-series chart with the shared header slots so it looks
  // like the same card with different lines.
  render: (slots: LevelTrendsChartSlots) => React.ReactNode
}

interface LevelTrendsChartProps {
  metric: CatalogueMetricKey
  onMetricChange: (next: CatalogueMetricKey) => void
  nameColumnHeader: string
  // The level's own line. `null` when the backend has no series for this level
  // (event types, until `trends/event-types` exists). `period` narrows the plotted axis
  // to the selected window's months; `source` may span more than that.
  total: { item: TrendsChartSeriesItem; source: TrendsSeriesSourceWithDenominator | undefined; period?: TrendsSelectedPeriod } | null
  isTotalLoading?: boolean
  // The total series request failed: Total shows an error (with a retry when
  // `onTotalRetry` is given) instead of the empty-state copy.
  isTotalError?: boolean
  onTotalRetry?: () => void
  // Empty-state copy when `total` is null and nothing else can be shown.
  totalUnavailableMessage?: string
  split?: LevelTrendsChartSplit
  // Denominator for the tracked-set caption under the adoption chart.
  cohortSize: number | null
  embedded?: boolean
}

const TITLE_KEYS: Record<CatalogueMetricKey, string> = {
  adoption: 'market-analytics:blueprint_trends_adoption_title',
  density: 'market-analytics:blueprint_trends_density_title',
  activeEvents: 'market-analytics:blueprint_trends_active_events_title',
}

/**
 * The one trend chart of a detail page (blueprint family, event type, event
 * category), agreed in the 2026-09-07 review to replace the stacked "own
 * series" + "children series" charts: metric lens as tabs, a `Total | Split by
 * …` toggle in the header, and one canvas. Total is the level's own
 * deduplicated series; Split hands the card to the children chart.
 */
export const LevelTrendsChart: React.FC<LevelTrendsChartProps> = ({
  metric,
  onMetricChange,
  nameColumnHeader,
  total,
  isTotalLoading = false,
  isTotalError = false,
  onTotalRetry,
  totalUnavailableMessage,
  split,
  cohortSize,
  embedded = false,
}) => {
  const { t } = useTranslation()

  const splitAvailable = Boolean(split && !split.unavailableMessage)
  // Only the user's explicit pick is state; the default is derived every render so
  // it follows the data. Total is the default whenever it exists or is on its way
  // (`total` is null while its request is in flight), and a failed total stays on
  // Total to show the error; Split only stands in when the level has no total series
  // at all. An unavailable Split always falls back to Total.
  const [userMode, setUserMode] = useState<LevelTrendsChartMode | null>(null)
  const totalExpected = Boolean(total) || isTotalLoading || isTotalError
  const defaultMode: LevelTrendsChartMode = totalExpected || !splitAvailable ? 'total' : 'split'
  const mode: LevelTrendsChartMode = (userMode ?? defaultMode) === 'split' && splitAvailable ? 'split' : 'total'

  const handleModeChange = useCallback((_: React.MouseEvent<HTMLElement>, next: LevelTrendsChartMode | null) => {
    if (next !== null) setUserMode(next)
  }, [])

  const tabs = useMemo(() => <MetricLensToggle value={metric} onChange={onMetricChange} variant="tabs" />, [metric, onMetricChange])

  const headerControls = useMemo(() => {
    if (!split) return null
    const group = (
      <ToggleButtonGroup
        value={mode}
        exclusive
        size="small"
        onChange={handleModeChange}
        aria-label={t('market-analytics:trend_split_label')}
        sx={compactToggleGroupSx}
      >
        <ToggleButton value="total" disabled={!totalExpected}>
          {t('market-analytics:trend_split_total')}
        </ToggleButton>
        <ToggleButton value="split" disabled={!splitAvailable}>
          {split.label}
        </ToggleButton>
      </ToggleButtonGroup>
    )
    return split.unavailableMessage ? (
      <GRTooltip content={split.unavailableMessage} styles={{ display: 'inline-flex' }}>
        {group}
      </GRTooltip>
    ) : (
      group
    )
  }, [split, mode, handleModeChange, t, totalExpected, splitAvailable])

  // Cohort context under the adoption line: the denominator it is measured against.
  const footer = useMemo(() => {
    if (metric !== 'adoption') return null
    return <TrackedSetDisclaimer cohortSize={cohortSize} />
  }, [metric, cohortSize])

  // Keyed on the fields the card reads rather than `total` itself, which callers
  // usually build inline: a fresh `items`/`seriesById` would defeat the card's memo
  // and redraw the canvas on every parent render.
  const totalItem = total?.item
  const totalItemId = totalItem?.id
  const totalItemName = totalItem?.name
  const totalItemDescription = totalItem?.description
  const totalSource = total?.source
  const items = useMemo<TrendsChartSeriesItem[]>(
    () => (totalItemId !== undefined && totalItemName !== undefined ? [{ id: totalItemId, name: totalItemName, description: totalItemDescription }] : []),
    [totalItemId, totalItemName, totalItemDescription]
  )
  const seriesById = useMemo(
    () => (totalItemId !== undefined ? { [totalItemId]: pickTrendsSeriesForMetric(totalSource, metric) } : {}),
    [totalItemId, totalSource, metric]
  )

  if (mode === 'split' && split) {
    return <>{split.render({ tabs, headerControls, footer, embedded })}</>
  }

  return (
    <TrendsLineChartCard
      title={t(TITLE_KEYS[metric])}
      ariaLabel={t(TITLE_KEYS[metric])}
      nameColumnHeader={nameColumnHeader}
      items={items}
      seriesById={seriesById}
      metric={metric}
      selectedPeriod={total?.period}
      isLoading={isTotalLoading}
      isError={isTotalError}
      onRetry={onTotalRetry}
      tabs={tabs}
      headerControls={headerControls}
      footer={footer}
      emptyMessage={totalUnavailableMessage}
      embedded={embedded}
    />
  )
}
